import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useSplitEnabled } from '@buffer-mono/features'
import { Flex, Loader, Separator, VisuallyHidden } from '@buffer-mono/popcorn'

import getErrorBoundary from '~publish/components/ErrorBoundary'
import { useLinkShorteningDisabledOverride } from '~publish/hooks/useLinkShorteningDisabledOverride'
import { actions as profileSidebarActions } from '~publish/legacy/profile-sidebar/reducer'
import {
  selectCurrentProfile,
  selectProfileById,
} from '~publish/legacy/profile-sidebar/selectors'
import ProfilesDisconnectedBanner from '~publish/legacy/profiles-disconnected-banner'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { BulkPostSetting } from './components/BulkPostUpload'
import { GoogleAnalytics } from './components/GoogleAnalytics'
import { LinkedInVerifiedSkills } from './components/LinkedInVerifiedSkills'
import {
  LinkShortening,
  SHORTENER_DISABLED_SERVICES,
} from './components/LinkShortening'
import { QueueActions } from './components/QueueActions'
import { RemindersSettings } from './components/RemindersSettings'
import { TrendingTopicsSetting } from './components/TrendingTopicsSetting'
import { selectGeneralSettings } from './selectors'

const ErrorBoundary = getErrorBoundary(true)

/**
 * Hook to ensure the correct profile is selected in Redux for the General Settings tab.
 *
 * The profile data comes from the bulk profiles fetch during APP_INIT.
 * This hook waits for that data to be available and selects the profile.
 *
 * Returns the profile directly to avoid an extra render cycle waiting for selection.
 */
const useProfileSync = (
  channelId: string,
): { isLoading: boolean; profile: ReturnType<typeof selectCurrentProfile> } => {
  const dispatch = useAppDispatch()

  const reduxProfileByChannelId = useAppSelector((state) =>
    selectProfileById(state, channelId),
  )
  const reduxSelectedProfile = useAppSelector(selectCurrentProfile)

  // Once the profile is available (from bulk fetch), select it
  useEffect(() => {
    if (reduxProfileByChannelId && channelId !== reduxSelectedProfile?.id) {
      dispatch(
        profileSidebarActions.selectProfile({
          profile: reduxProfileByChannelId,
        }),
      )
    }
  }, [dispatch, channelId, reduxProfileByChannelId, reduxSelectedProfile])

  // Use the profile by ID directly to avoid waiting for selection round-trip
  return {
    isLoading: !reduxProfileByChannelId,
    profile: reduxProfileByChannelId,
  }
}

const GeneralSettings = (): React.JSX.Element => {
  const { id: channelId } = useParams<{ id: string }>()
  const channel = useSelectedChannel()
  const { isLoading, profile } = useProfileSync(channelId ?? '')
  const { isEnabled: isLinkedInVerifiedSkillsEnabled } = useSplitEnabled(
    'linkedin-verified-skills',
  )

  const generalSettings = useAppSelector(selectGeneralSettings)
  const linkShorteningDisabled = useLinkShorteningDisabledOverride()

  if (isLoading || !profile) {
    return (
      <Flex
        justify="center"
        align="center"
        fullWidth
        style={{ padding: '2rem' }}
      >
        <Loader />
      </Flex>
    )
  }

  const isManager = canPerform(channel, 'manageChannelSettings')
  const isThreadsChannel = profile.service === 'threads'

  const isInstagramPersonalProfile =
    profile.service === 'instagram' && profile.service_type === 'profile'
  const isRemindersOnly =
    generalSettings.isRemindersOnly || isInstagramPersonalProfile
  const isDisconnectedProfile = profile.isDisconnected
  const supportsMobileReminders =
    profile.service === 'instagram' ||
    profile.service === 'youtube' ||
    profile.service === 'tiktok' ||
    (profile.service === 'facebook' && profile.service_type === 'group')

  const showGoogleAnalytics = !SHORTENER_DISABLED_SERVICES.includes(
    profile.service,
  )
  const showLinkedInVerifiedSkillsSection =
    isLinkedInVerifiedSkillsEnabled && profile.service === 'linkedin'

  return (
    <ErrorBoundary>
      {isDisconnectedProfile && <ProfilesDisconnectedBanner />}
      <Flex direction="column" data-testid="general-settings-wrapper">
        <VisuallyHidden as="h2">General</VisuallyHidden>
        {supportsMobileReminders && (
          <RemindersSettings isRemindersOnly={isRemindersOnly} />
        )}

        {!linkShorteningDisabled && (
          <>
            <LinkShortening />
          </>
        )}

        {showGoogleAnalytics && (
          <>
            <Separator />
            <GoogleAnalytics />
          </>
        )}
        {isThreadsChannel && (
          <>
            <Separator />
            <TrendingTopicsSetting />
          </>
        )}
        {isManager && (
          <>
            <Separator />
            <BulkPostSetting />
          </>
        )}
        {showLinkedInVerifiedSkillsSection && (
          <>
            <Separator />
            <LinkedInVerifiedSkills key={profile.id} profile={profile} />
          </>
        )}
        {isManager && (
          <>
            <Separator />
            <QueueActions />
          </>
        )}
      </Flex>
    </ErrorBoundary>
  )
}

export { GeneralSettings }
